import { CurrencyAmount, Token } from '@kodiak-finance/sdk-core'
import CurrencyLogo from 'components/CurrencyLogo'
import { formatDistance } from 'date-fns'
import { useToken } from 'hooks/Tokens'
import { useApiTokenValue } from 'hooks/useTokenPrice'
import React, { useMemo } from 'react'
import styled from 'styled-components'
import { formatUTCDay } from 'utils/analytics/date'
import { formatAmount, formatDollarAmount } from 'utils/formatDollarAmt'
import { formatUnits } from 'viem'

const TooltipContainer = styled.div`
  background: ${({ theme }) => theme.backgroundSurface};
  border: 1px solid ${({ theme }) => theme.backgroundOutline};
  border-radius: 8px;
  padding: 12px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
  max-width: 340px;
  min-width: 280px;
`

const FlexRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
`

const TooltipHeader = styled.div`
  font-weight: 600;
  color: ${({ theme }) => theme.textPrimary};
  font-size: 14px;
  margin-bottom: 4px;
`

const TooltipSubHeader = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.textSecondary};
  margin-bottom: 8px;
`

const Divider = styled.hr`
  margin: 8px 0;
  border: 0.5px solid ${({ theme }) => theme.backgroundOutline};
`

const InfoRow = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 6px;
`

const Label = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.textSecondary};
`

const Value = styled.div`
  font-size: 12px;
  font-weight: 500;
  color: ${({ theme }) => theme.textPrimary};
  text-align: right;
`

const SuccessValue = styled(Value)`
  color: ${({ theme }) => theme.accentSuccess};
`

const WarningValue = styled(Value)`
  color: ${({ theme }) => theme.accentWarning};
`

interface BaultCompoundTooltipProps {
  active?: boolean
  payload?: any[]
  label?: any
  pricePoints?: any[]
  stakingToken?: any
  rewardVaultRewardToken?: Token | null
}

export const BaultCompoundTooltip: React.FC<BaultCompoundTooltipProps> = ({
  active,
  payload,
  label,
  pricePoints,
  stakingToken,
  rewardVaultRewardToken,
}) => {
  const data = payload?.[0]?.payload
  const originalCompound = data?.compoundNumber
    ? pricePoints?.find((p) => p.compoundNumber === data.compoundNumber)
    : null

  const wrapperAddress = originalCompound?.wrapperAddress
  const rawRewardAmount = originalCompound?.bgtAmount
  const wrapperToken = useToken(wrapperAddress || undefined)
  const rewardToken = rewardVaultRewardToken
  const rewardSymbol = rewardToken?.symbol || 'Reward'

  const stakingDecimals = stakingToken?.decimals || 18
  const wrapperTokenAmount = useMemo(() => {
    if (!stakingToken || !originalCompound?.wrapperTokenAmount || !wrapperToken) return null

    return CurrencyAmount.fromRawAmount(wrapperToken, originalCompound.wrapperTokenAmount)
  }, [wrapperToken, originalCompound?.wrapperTokenAmount, stakingToken])

  const { value: wrapperUsdValue } = useApiTokenValue(wrapperTokenAmount)

  const rewardAmount = useMemo(() => {
    if (!rawRewardAmount) return null

    if (!rewardToken) return null

    return CurrencyAmount.fromRawAmount(rewardToken, rawRewardAmount)
  }, [rawRewardAmount, rewardToken])

  const { value: rewardUsdValue } = useApiTokenValue(rewardAmount)

  if (!active || !payload || !payload.length) {
    return null
  }

  const isCompoundEvent = data.isCompoundEvent || payload.some((entry) => entry.name === 'Compound Event')

  if (isCompoundEvent && originalCompound) {
    const timestamp = parseInt(originalCompound.timestamp) * 1000
    const formattedDate = formatUTCDay(timestamp / 1000, 'MMM D, YYYY HH:mm')

    let timeSinceLastClaim = 'First Claim'
    if (originalCompound.previousClaimTimestamp && originalCompound.previousClaimTimestamp !== '0') {
      const previousDate = new Date(parseInt(originalCompound.previousClaimTimestamp) * 1000)
      timeSinceLastClaim = formatDistance(new Date(timestamp), previousDate, { addSuffix: false })
    }

    const compoundAmount = formatUnits(originalCompound.compoundAmount, stakingDecimals)

    const growthFromPrev = data.growthFromPrev ? parseFloat(data.growthFromPrev).toFixed(6) : null

    return (
      <TooltipContainer>
        <TooltipHeader>Compound #{originalCompound.compoundNumber}</TooltipHeader>
        <TooltipSubHeader>{formattedDate}</TooltipSubHeader>

        <Divider />

        <TooltipHeader>
          {originalCompound.isWrapperClaim ? `${rewardSymbol} Wrapper Claim` : `${rewardSymbol} Claim`}
        </TooltipHeader>

        <InfoRow>
          <Label>Staking Tokens Added:</Label>
          <Value>{`${parseFloat(compoundAmount).toFixed(6)}`}</Value>
        </InfoRow>

        {/* {usdValue && (
          <InfoRow>
            <Label>$ Value:</Label>
            <Value>${formatAmount(usdValue.toString())}</Value>
          </InfoRow>
        )} */}

        {!originalCompound.isWrapperClaim && rawRewardAmount && (
          <>
            <InfoRow>
              <Label>{rewardSymbol} Claimed:</Label>
              <Value>
                {rewardAmount?.toSignificant(6) || formatUnits(rawRewardAmount, rewardToken?.decimals || 18)}{' '}
                {rewardSymbol}
              </Value>
            </InfoRow>
            {rewardUsdValue && (
              <InfoRow>
                <Label>{rewardSymbol} USD Value:</Label>
                <Value>{formatDollarAmount(rewardUsdValue)}</Value>
              </InfoRow>
            )}
          </>
        )}

        {originalCompound.isWrapperClaim && (
          <>
            {wrapperAddress && (
              <InfoRow>
                <Label>Wrapper:</Label>
                <Value>
                  <FlexRow>
                    {wrapperToken && <CurrencyLogo currency={wrapperToken} size="18px" />}
                    {wrapperToken?.symbol || wrapperAddress.substring(0, 6) + '...' + wrapperAddress.substring(38)}
                  </FlexRow>
                </Value>
              </InfoRow>
            )}

            {originalCompound.wrapperTokenAmount && (
              <>
                <InfoRow>
                  <Label>Wrapper Minted:</Label>
                  <Value>
                    <FlexRow>
                      {formatAmount(parseFloat(formatUnits(originalCompound.wrapperTokenAmount, 18)))}
                      {wrapperToken?.symbol && ` ${wrapperToken.symbol}`}
                    </FlexRow>
                  </Value>
                </InfoRow>
                {wrapperUsdValue && (
                  <InfoRow>
                    <Label>Wrapper USD Value:</Label>
                    <Value>{formatDollarAmount(wrapperUsdValue)}</Value>
                  </InfoRow>
                )}
              </>
            )}
          </>
        )}

        <Divider />

        <TooltipHeader>Result</TooltipHeader>

        {growthFromPrev && (
          <InfoRow>
            <Label>Growth:</Label>
            <SuccessValue>+{growthFromPrev}%</SuccessValue>
          </InfoRow>
        )}

        <InfoRow>
          <Label>New Share Price:</Label>
          <Value>{parseFloat(data.sharePrice).toFixed(8)}</Value>
        </InfoRow>

        <InfoRow>
          <Label>Time Since Last Claim:</Label>
          <WarningValue>{timeSinceLastClaim}</WarningValue>
        </InfoRow>
      </TooltipContainer>
    )
  }

  return (
    <TooltipContainer>
      <TooltipSubHeader>{formatUTCDay(Number(label) / 1000, 'MMM D, YYYY HH:mm')}</TooltipSubHeader>

      {payload.map((entry: any, index: number) => (
        <InfoRow key={`tooltip-${index}`}>
          <Label style={{ color: entry.color }}>{entry.name}:</Label>
          <Value>
            {entry.name.toLowerCase().includes('apy') || entry.name.toLowerCase().includes('growth')
              ? `${parseFloat(entry.value).toFixed(4)}%`
              : entry.name.toLowerCase().includes('price')
              ? parseFloat(entry.value).toFixed(8)
              : formatAmount(entry.value)}
          </Value>
        </InfoRow>
      ))}

      {data.compoundNumber && !isCompoundEvent && (
        <InfoRow>
          <Label>Compound #:</Label>
          <Value>{data.compoundNumber}</Value>
        </InfoRow>
      )}
    </TooltipContainer>
  )
}

export default BaultCompoundTooltip
